import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

/**
 * Shared shell for the marketing/SEO landing pages introduced in
 * the Phase-0b1 AI-discoverability push.
 *
 * Every SEO page ("/best-swim-school-software",
 * "/swim-lesson-registration-software", "/for-swim-instructors",
 * "/vs/jackrabbit-swim") uses this shell so we get:
 *   - identical top/bottom chrome (matches /security, /privacy)
 *   - per-page <title>, <meta description>, <link rel=canonical>
 *   - per-page JSON-LD block (passed in as a plain JS object)
 *   - a single, consistent primary CTA
 *
 * Rationale: keeps individual SEO pages small and content-focused,
 * and guarantees crawlers see a stable, structured page frame.
 */
export const SeoPageShell = ({
    title,
    description,
    canonical,
    // ogTitle / ogDescription are accepted for back-compat but og:* now
    // mirror title/description (Phase 126 head manager).
    ogTitle: _ogTitle, // eslint-disable-line no-unused-vars
    ogDescription: _ogDescription, // eslint-disable-line no-unused-vars
    jsonLd,
    breadcrumb,
    faqJsonLd,
    children,
    testId,
}) => {
    return (
        <div className="min-h-screen bg-[#f4f7fb]" data-testid={testId}>
            <Seo
                title={title}
                description={description}
                canonical={canonical || undefined}
                jsonLd={[jsonLd, breadcrumb, faqJsonLd].filter(Boolean)}
            />

            {/* Header — mirrors /security /privacy /terms exactly */}
            <header className="border-b border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
                    <Link to="/" className="flex items-center gap-2" data-testid="seo-page-home-link">
                        <Logo />
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to="/blog"><Button variant="ghost" size="sm" data-testid="seo-page-nav-blog">Blog</Button></Link>
                        <Link to="/login"><Button variant="ghost" size="sm" data-testid="seo-page-nav-login">Log in</Button></Link>
                        <Link to="/signup"><Button size="sm" className="sd-cta sd-focus" data-testid="seo-page-nav-signup">Sign up</Button></Link>
                    </div>
                </div>
            </header>

            {children}

            {/* Bottom CTA band — every SEO page gets one so cold
                traffic and LLM referrers hit a clear next step */}
            <section className="border-t border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">
                        Ready to run your swim school on software built by a swim instructor?
                    </h2>
                    <p className="mt-3 text-[#1a3a5c]">
                        Free 30-day trial. Cancel any time.
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                        <Link to="/signup">
                            <Button className="sd-cta sd-focus h-12 rounded-xl px-6 text-base" data-testid="seo-page-cta-signup">
                                Start my free trial <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                        </Link>
                        <Link to="/">
                            <Button variant="outline" className="h-12 rounded-xl border-[#0a1628]/20 px-6 text-base" data-testid="seo-page-cta-tour">
                                Take the product tour
                            </Button>
                        </Link>
                    </div>
                    <p className="mt-6 text-xs text-[#1a3a5c]/70">
                        SplashPad · The swim lesson registration software designed by a swim instructor, for swim instructors.
                    </p>
                </div>
            </section>
        </div>
    );
};
